import { CredentialRequest } from "@/models/credentialRequest";
import { ColumnDef, Column } from "@tanstack/react-table";
import { StatusBadge } from "@/components/atoms/StatusBadge";

const GetSortButton = (column: Column<CredentialRequest>, label: string) => {
  return (
    <div
      onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      className="cursor-pointer font-semibold"
    >
      {label}
    </div>
  );
}

interface ColumnOptions {
  hideDistrict?: boolean;
  hideRegion?: boolean;
}

export const credentialRequestColumns = (options: ColumnOptions = {}): ColumnDef<CredentialRequest>[] => {
  const { hideDistrict = false, hideRegion = false } = options;
  
  const baseColumns: ColumnDef<CredentialRequest>[] = [
    {
      accessorKey: "code",
      header: ({ column }) => GetSortButton(column, "Código"),
      cell: ({ row }) => {
        const code = row.original.code;
        if (!code) return '-';
        return (
          <span className="font-mono text-sm">
            {code}
          </span>
        );
      }
    },
    {
      accessorKey: "status",
      header: ({ column }) => GetSortButton(column, "Estado"),
      cell: ({ row }) => {
        const status = row.original.status;
        
        return <StatusBadge status={status} variant="compact" />;
      }
    },
  ];

  // Conditionally add district column
  if (!hideDistrict) {
    baseColumns.push({
      accessorKey: "district.name",
      enableGlobalFilter: false,
      enableSorting: false,
      header: "Distrito",
      cell: ({ row }) => row.original.district?.name || '-'
    });
  }

  // Conditionally add region column
  if (!hideRegion) {
    baseColumns.push({
      accessorKey: "region.name",
      enableGlobalFilter: false,
      enableSorting: false,
      header: "Región",
      cell: ({ row }) => row.original.region?.name || '-'
    });
  }

  // Add remaining columns
  baseColumns.push(
    {
      accessorKey: "created_by",
      enableGlobalFilter: false,
      enableSorting: false,
      header: "Creado por",
      cell: ({ row }) => row.original.creator?.full_name || '-'
    },
    {
      accessorKey: "created_at",
      header: ({ column }) => GetSortButton(column, "Fecha creación"),
      cell: ({ row }) => {
        const date = row.original.created_at;
        if (!date) return '-';
        return new Date(date).toLocaleDateString('es-BO', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
      }
    },
    {
      accessorKey: "minister_count",
      header: ({ column }) => GetSortButton(column, "Ministros"),
      cell: ({ row }) => {
        // Supabase returns minister_count as an array of objects: [{count: n}]
        const ministerCount = row.original.minister_count;
        let count = 0;
        
        if (typeof ministerCount === 'number') {
          count = ministerCount;
        } else if (Array.isArray(ministerCount) && ministerCount.length > 0) {
          count = ministerCount[0].count || 0;
        }
        
        return (
          <span className="inline-flex size-8 items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-800">
            {count}
          </span>
        );
      }
    },
    {
      accessorKey: "submitted_at",
      header: ({ column }) => GetSortButton(column, "Fecha envío"),
      cell: ({ row }) => {
        const date = row.original.submitted_at;
        if (!date) return '-';
        return new Date(date).toLocaleDateString('es-BO', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
      }
    },
    {
      accessorKey: "approved_at",
      header: ({ column }) => GetSortButton(column, "Fecha aprobación"),
      cell: ({ row }) => {
        const date = row.original.approved_at;
        if (!date) return '-';
        return new Date(date).toLocaleDateString('es-BO', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
      }
    }
  );

  return baseColumns;
};
